import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  RobotOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  CrownOutlined,
  ThunderboltOutlined,
  RocketOutlined,
  FileTextOutlined,
  ApiOutlined,
  BarChartOutlined,
  CustomerServiceOutlined,
  PictureOutlined,
  LoadingOutlined,
  InfoCircleOutlined,
  GlobalOutlined,
  PlusOutlined,
  DeleteOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import { ordersService, pricingService, productsService } from '../../services';
import { PricingPlan } from '../../services/pricing.service';
import { ProductCategory } from '../../constants/productCategory';
import { useNotificationContext } from '../../contexts/NotificationContext';
import {
  ChatbotPlanConfig,
  ChatbotFormData,
  ChatbotFeatures,
} from '../../types/chatbot.type';


// Fallback si el API de pricing no responde.
// Espejo de KubiyPricingCatalog-prod (FAMILY#AI): mantener sincronizado con el catálogo.
const PLANS: ChatbotPlanConfig[] = [
  {
    id: 'KY_EDGE',
    name: 'Kubiy AI Edge',
    price: 289,
    currency: 'MXN',
    messagesLimit: 15000,
    documentsLimit: 10,
    storageMbLimit: 100,
    overageMessagePrice: 0.1,
    features: {
      pdfSupport: true,
      excelSupport: true,
      imageSupport: false,
      apiAccess: true,
      analyticsEnabled: false,
    },
    featureLabels: [
      '15,000 mensajes/mes',
      '10 documentos',
      '100 MB de almacenamiento',
      'Soporte PDF y Excel',
      'Widget + API Access',
      '$0.10 MXN por mensaje extra',
    ],
  },
  {
    id: 'KY_CORE',
    name: 'Kubiy AI Core',
    price: 579,
    currency: 'MXN',
    messagesLimit: 30000,
    documentsLimit: 20,
    storageMbLimit: 200,
    overageMessagePrice: 0.1,
    highlighted: true,
    features: {
      pdfSupport: true,
      excelSupport: true,
      imageSupport: false,
      apiAccess: true,
      analyticsEnabled: false,
    },
    featureLabels: [
      '30,000 mensajes/mes',
      '20 documentos',
      '200 MB de almacenamiento',
      'Soporte PDF y Excel',
      'Widget + API Access',
      '$0.10 MXN por mensaje extra',
    ],
  },
  {
    id: 'KY_PULSE',
    name: 'Kubiy AI Pulse',
    price: 1159,
    currency: 'MXN',
    messagesLimit: 70000,
    documentsLimit: 50,
    storageMbLimit: 500,
    overageMessagePrice: 0.1,
    features: {
      pdfSupport: true,
      excelSupport: true,
      imageSupport: false,
      apiAccess: true,
      analyticsEnabled: false,
    },
    featureLabels: [
      '70,000 mensajes/mes',
      '50 documentos',
      '500 MB de almacenamiento',
      'Soporte PDF y Excel',
      'Widget + API Access',
      '$0.10 MXN por mensaje extra',
    ],
  },
];

const mapApiPlan = (p: PricingPlan): ChatbotPlanConfig => {
  const cfg = (p.providerConfiguration as any) || {};
  const res = (p.includedResources as any) || {};
  const overage = ((p.raw as any)?.overagePricing) || {};
  // FAMILY#AI usa messages/documents/storageMb; AI_SERVICE usaba messagesPerMonth/documentsLimit
  const msgs: number = Number(res.messages ?? res.messagesPerMonth ?? 500);
  const docs: number = Number(res.documents ?? res.documentsLimit ?? 10);
  const storageMb: number | undefined = res.storageMb != null ? Number(res.storageMb) : undefined;
  const overageMinor = Number(overage?.message?.unitPrice);
  const overageMessagePrice = Number.isFinite(overageMinor) && overageMinor > 0 ? overageMinor / 100 : undefined;
  // FAMILY#AI no define flags de features: mismo widget para todos los planes,
  // se diferencia por límites. PDF/Excel y API son parte del producto base.
  const features = {
    pdfSupport: cfg.pdfSupport != null ? !!cfg.pdfSupport : true,
    excelSupport: cfg.excelSupport != null ? !!cfg.excelSupport : true,
    imageSupport: !!cfg.imageSupport,
    apiAccess: cfg.apiAccess != null ? !!cfg.apiAccess : true,
    analyticsEnabled: !!cfg.analyticsEnabled,
  };
  const labels: string[] = [`${msgs.toLocaleString()} mensajes/mes`, `${docs.toLocaleString()} documentos`];
  if (storageMb != null) labels.push(`${storageMb.toLocaleString()} MB de almacenamiento`);
  if (features.pdfSupport && features.excelSupport) labels.push('Soporte PDF y Excel');
  if (features.imageSupport) labels.push('Soporte de imágenes');
  if (features.apiAccess) labels.push('Widget + API Access');
  if (features.analyticsEnabled) labels.push('Analytics');
  if (overageMessagePrice != null) labels.push(`$${overageMessagePrice.toFixed(2)} MXN por mensaje extra`);
  return {
    id: p.planCode,
    name: p.planName || p.planCode,
    price: p.priceAmount ?? (p.priceAmountMinor != null ? p.priceAmountMinor / 100 : 0),
    currency: p.currency || 'MXN',
    messagesLimit: msgs,
    documentsLimit: docs,
    storageMbLimit: storageMb,
    overageMessagePrice,
    highlighted: p.planCode === 'KY_CORE' || p.planCode === 'KY_PRO',
    features,
    featureLabels: labels,
  };
};

// Iconos por feature
const FEATURE_ICONS: Record<string, React.ReactNode> = {
  'mensaje extra': <ThunderboltOutlined />,
  'mensajes/mes': <ThunderboltOutlined />,
  'almacenamiento': <DatabaseOutlined />,
  'documentos': <FileTextOutlined />,
  'PDF': <FileTextOutlined />,
  'Excel': <FileTextOutlined />,
  'imágenes': <PictureOutlined />,
  'API': <ApiOutlined />,
  'Analytics': <BarChartOutlined />,
  'Widget': <RobotOutlined />,
  'Soporte prioritario': <CustomerServiceOutlined />,
  'formatos': <FileTextOutlined />,
};

const getFeatureIcon = (label: string): React.ReactNode => {
  for (const [key, icon] of Object.entries(FEATURE_ICONS)) {
    if (label.includes(key)) return icon;
  }
  return <CheckCircleFilled />;
};

// Componente de tarjeta de plan
interface PlanCardProps {
  plan: ChatbotPlanConfig;
  isSelected: boolean;
  onSelect: () => void;
}

const PlanCard: React.FC<PlanCardProps> = ({ plan, isSelected, onSelect }) => {
  return (
    <button
      onClick={onSelect}
      className={`relative flex flex-col p-6 border-2 rounded-xl transition-all text-left w-full ${isSelected
        ? 'border-black dark:border-white bg-gray-50 dark:bg-gray-800 shadow-lg'
        : plan.highlighted
          ? 'border-gray-300 dark:border-gray-500 hover:border-gray-400 dark:hover:border-gray-400 shadow-md'
          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
        }`}
    >
      {plan.highlighted && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-black dark:bg-white text-white dark:text-black text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
            <CrownOutlined /> Más popular
          </span>
        </div>
      )}

      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          {/edge|starter/.test(plan.id.toLowerCase()) && <RocketOutlined className="text-lg text-blue-500" />}
          {/core|pro$/.test(plan.id.toLowerCase()) && <ThunderboltOutlined className="text-lg text-purple-500" />}
          {/pulse|enterprise/.test(plan.id.toLowerCase()) && <CrownOutlined className="text-lg text-amber-500" />}
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">{plan.name}</h3>
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold text-gray-900 dark:text-white">
            {plan.currency === 'MXN'
              ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(plan.price)
              : `$${plan.price}`}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">/mes</span>
        </div>
      </div>

      <div className="mb-4 space-y-1">
        <div className="flex items-center gap-2 text-sm">
          <ThunderboltOutlined className="text-gray-400" />
          <span className="text-gray-700 dark:text-gray-300">
            <strong>{plan.messagesLimit.toLocaleString()}</strong> mensajes/mes
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <FileTextOutlined className="text-gray-400" />
          <span className="text-gray-700 dark:text-gray-300">
            <strong>{plan.documentsLimit.toLocaleString()}</strong> documentos
          </span>
        </div>
        {plan.storageMbLimit != null && (
          <div className="flex items-center gap-2 text-sm">
            <DatabaseOutlined className="text-gray-400" />
            <span className="text-gray-700 dark:text-gray-300">
              <strong>{plan.storageMbLimit.toLocaleString()} MB</strong> almacenamiento
            </span>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 dark:border-gray-600 my-3" />

      <ul className="space-y-2 flex-1">
        {plan.featureLabels.map((label, idx) => (
          <li key={idx} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <span className="text-green-500 flex-shrink-0">{getFeatureIcon(label)}</span>
            {label}
          </li>
        ))}
      </ul>

      <div className="mt-6">
        <div
          className={`w-full py-2.5 rounded-lg font-medium text-center transition-all ${isSelected
            ? 'bg-black dark:bg-white text-white dark:text-black'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
        >
          {isSelected ? '✓ Seleccionado' : 'Seleccionar'}
        </div>
      </div>
    </button>
  );
};

// Componente principal
const CreateChatbotIA: React.FC = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useNotificationContext();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<ChatbotFormData>({
    plan: null,
    serviceName: '',
    description: '',
    apiAccess: false,
    vectorStore: 'S3',
    allowedOrigins: [],
  });

  const [isCreating, setIsCreating] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newOrigin, setNewOrigin] = useState('');

  const [apiPlans, setApiPlans] = useState<PricingPlan[]>([]);
  const [productId, setProductId] = useState('prod-ai-assistant-starter');
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    const load = async () => {
      setIsLoadingPlans(true);
      try {
        const [plansRes, productsRes] = await Promise.all([
          pricingService.getPlans('AI', 'GENERIC'),
          productsService.getProductsByCategory(ProductCategory.AI_SERVICE),
        ]);
        setApiPlans(plansRes);
        const ps = Array.isArray(productsRes?.products) ? productsRes.products : [];
        if (ps[0]?.id) setProductId(ps[0].id);
      } catch {
        // fallback to hardcoded PLANS + default productId
      } finally {
        setIsLoadingPlans(false);
      }
    };
    load();
  }, []);

  const activePlans: ChatbotPlanConfig[] = useMemo(() => {
    if (apiPlans.length === 0) return PLANS;
    return [...apiPlans]
      .sort((a, b) => (a.sortOrder ?? 99) - (b.sortOrder ?? 99))
      .map(mapApiPlan);
  }, [apiPlans]);

  // Plan seleccionado
  const selectedPlan = useMemo(() => {
    return activePlans.find((p) => p.id === formData.plan) || null;
  }, [formData.plan, activePlans]);

  // Validaciones
  const validateServiceName = (name: string): string | null => {
    if (!name.trim()) return 'El nombre del servicio es requerido';
    if (name.trim().length < 3) return 'Mínimo 3 caracteres';
    if (name.trim().length > 50) return 'Máximo 50 caracteres';
    return null;
  };

  // Manejar cambio de nombre
  const handleNameChange = (value: string) => {
    setFormData((prev) => ({ ...prev, serviceName: value }));
    const nameError = validateServiceName(value);
    setErrors((prev) => {
      const next = { ...prev };
      if (nameError) next.serviceName = nameError;
      else delete next.serviceName;
      return next;
    });
  };

  const addOrigin = () => {
    const origin = newOrigin.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/+$/, '');
    if (!origin) return;
    if (formData.allowedOrigins.includes(origin)) return;
    if (formData.allowedOrigins.length >= 20) return;
    setFormData(prev => ({ ...prev, allowedOrigins: [...prev.allowedOrigins, origin] }));
    setNewOrigin('');
  };

  const removeOrigin = (index: number) => {
    setFormData(prev => ({ ...prev, allowedOrigins: prev.allowedOrigins.filter((_, i) => i !== index) }));
  };

  // Seleccionar plan
  const handleSelectPlan = (planId: string) => {
    const plan = activePlans.find((p) => p.id === planId);
    setFormData((prev) => ({
      ...prev,
      plan: planId,
      apiAccess: plan?.features.apiAccess ? prev.apiAccess : false,
    }));
  };

  // Navegación entre steps
  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  // Validar si el step actual es válido para avanzar
  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.plan !== null;
      case 2:
        return formData.serviceName.trim().length >= 3 && formData.serviceName.trim().length <= 50 && formData.allowedOrigins.length >= 1;
      case 3:
        return true;
      default:
        return false;
    }
  };

  // Crear servicio
  const handleCreate = async () => {
    if (!selectedPlan) return;

    setIsCreating(true);
    try {
      const features: ChatbotFeatures = {
        ...selectedPlan.features,
        apiAccess: formData.apiAccess || selectedPlan.features.apiAccess,
      };

      const orderData = {
        productId,
        quantity: 1,
        metadata: {
          type: 'AI_SERVICE',
          serviceName: formData.serviceName.trim(),
          description: formData.description.trim() || '',
          plan: selectedPlan.id,
          planCode: selectedPlan.id,
          pricingSelection: {
            typeFamily: 'AI',
            osType: 'GENERIC',
            osFamily: 'GENERIC',
            planCode: selectedPlan.id,
          },
          messagesLimit: selectedPlan.messagesLimit,
          documentsLimit: selectedPlan.documentsLimit,
          storageMbLimit: selectedPlan.storageMbLimit,
          vectorStore: formData.vectorStore,
          features: {
            pdfSupport: selectedPlan.features.pdfSupport,
            excelSupport: selectedPlan.features.excelSupport,
            imageSupport: selectedPlan.features.imageSupport,
            apiAccess: formData.apiAccess || selectedPlan.features.apiAccess,
            analyticsEnabled: selectedPlan.features.analyticsEnabled,
          },
          allowedOrigins: formData.allowedOrigins,
        },
      };

      const createdOrder = await ordersService.create(orderData);
      success('Servicio de Chatbot IA creado exitosamente');
      navigate(`/payment-selection/${createdOrder.id}`);
    } catch (err: any) {
      console.error('Error creating AI service order:', err);
      showError(err?.message || 'Error al crear el servicio. Intente nuevamente.');
      setIsCreating(false);
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/chatbot-ia')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
        >
          <ArrowLeftOutlined />
          <span className="text-sm font-medium">Volver</span>
        </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Crear Chatbot IA</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Configura tu asistente de inteligencia artificial paso a paso.
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[
            { num: 1, label: 'Seleccionar plan' },
            { num: 2, label: 'Configuración' },
            { num: 3, label: 'Revisión' },
          ].map((s, idx) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${step >= s.num
                    ? 'bg-black dark:bg-white text-white dark:text-black'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}
                >
                  {step > s.num ? <CheckCircleOutlined /> : s.num}
                </div>
                <span
                  className={`text-xs mt-2 font-medium ${step >= s.num ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
                    }`}
                >
                  {s.label}
                </span>
              </div>
              {idx < 2 && (
                <div
                  className={`h-1 flex-1 mx-2 rounded transition-all ${step > s.num ? 'bg-black dark:bg-white' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Content */}
      <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700 p-8 mb-6">
        {/* Step 1: Selección de Plan */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Selecciona un plan *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {isLoadingPlans ? (
                  <div className="col-span-3 text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
                    <LoadingOutlined className="mr-2" />Cargando planes…
                  </div>
                ) : (
                  activePlans.map((plan) => (
                    <PlanCard
                      key={plan.id}
                      plan={plan}
                      isSelected={formData.plan === plan.id}
                      onSelect={() => handleSelectPlan(plan.id)}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Tabla comparativa */}
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2 text-sm">
                  <InfoCircleOutlined /> Comparar planes
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                      <th className="text-left p-3 font-medium text-gray-600 dark:text-gray-400">Feature</th>
                      {activePlans.map((plan) => (
                        <th key={plan.id} className="text-center p-3 font-medium text-gray-900 dark:text-white">
                          {plan.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-100 dark:border-gray-700">
                      <td className="p-3 text-gray-600 dark:text-gray-400">Precio mensual</td>
                      {activePlans.map((plan) => (
                        <td key={plan.id} className="text-center p-3 font-bold text-gray-900 dark:text-white">
                          {plan.currency === 'MXN'
                            ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(plan.price)
                            : `$${plan.price}`}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b border-gray-100 dark:border-gray-700">
                      <td className="p-3 text-gray-600 dark:text-gray-400">Mensajes/mes</td>
                      {activePlans.map((plan) => (
                        <td key={plan.id} className="text-center p-3 text-gray-900 dark:text-white">{plan.messagesLimit.toLocaleString()}</td>
                      ))}
                    </tr>
                    <tr className="border-b border-gray-100 dark:border-gray-700">
                      <td className="p-3 text-gray-600 dark:text-gray-400">Documentos</td>
                      {activePlans.map((plan) => (
                        <td key={plan.id} className="text-center p-3 text-gray-900 dark:text-white">{plan.documentsLimit.toLocaleString()}</td>
                      ))}
                    </tr>
                    {activePlans.some((plan) => plan.storageMbLimit != null) && (
                      <tr className="border-b border-gray-100 dark:border-gray-700">
                        <td className="p-3 text-gray-600 dark:text-gray-400">Almacenamiento</td>
                        {activePlans.map((plan) => (
                          <td key={plan.id} className="text-center p-3 text-gray-900 dark:text-white">
                            {plan.storageMbLimit != null ? `${plan.storageMbLimit.toLocaleString()} MB` : '—'}
                          </td>
                        ))}
                      </tr>
                    )}
                    {activePlans.some((plan) => plan.overageMessagePrice != null) && (
                      <tr className="border-b border-gray-100 dark:border-gray-700">
                        <td className="p-3 text-gray-600 dark:text-gray-400">Mensaje extra</td>
                        {activePlans.map((plan) => (
                          <td key={plan.id} className="text-center p-3 text-gray-900 dark:text-white">
                            {plan.overageMessagePrice != null ? `$${plan.overageMessagePrice.toFixed(2)} MXN` : '—'}
                          </td>
                        ))}
                      </tr>
                    )}
                    {[
                      { key: 'pdfSupport', label: 'Soporte PDF' },
                      { key: 'excelSupport', label: 'Soporte Excel' },
                      { key: 'imageSupport', label: 'Soporte Imágenes' },
                      { key: 'apiAccess', label: 'API Access' },
                      { key: 'analyticsEnabled', label: 'Analytics' },
                    ].map((feature) => (
                      <tr key={feature.key} className="border-b border-gray-100 dark:border-gray-700">
                        <td className="p-3 text-gray-600 dark:text-gray-400">{feature.label}</td>
                        {activePlans.map((plan) => (
                          <td key={plan.id} className="text-center p-3">
                            {plan.features[feature.key as keyof ChatbotFeatures] ? (
                              <CheckCircleFilled className="text-green-500" />
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600">—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Configuración */}
        {step === 2 && (
          <div className="space-y-6">
            {/* Nombre del servicio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nombre del servicio *
              </label>
              <input
                type="text"
                value={formData.serviceName}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Ej: Asistente de Soporte Técnico"
                maxLength={50}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${errors.serviceName
                  ? 'border-red-500 dark:border-red-400'
                  : 'border-gray-300 dark:border-gray-600'
                  }`}
              />
              <div className="flex justify-between mt-1">
                {errors.serviceName ? (
                  <p className="text-xs text-red-500">{errors.serviceName}</p>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">Entre 3 y 50 caracteres</p>
                )}
                <p className="text-xs text-gray-400">{formData.serviceName.length}/50</p>
              </div>
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Descripción <span className="text-gray-400">(opcional)</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="¿Qué hace tu asistente?"
                maxLength={200}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
              />
              <div className="flex justify-end mt-1">
                <p className="text-xs text-gray-400">{formData.description.length}/200</p>
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Almacenamiento de vectores
              </h3>

              <div className="space-y-2">
                {/* <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <input
                    type="radio"
                    name="vectorStore"
                    value="OPENSEARCH_SERVERLESS"
                    checked={formData.vectorStore === 'OPENSEARCH_SERVERLESS'}
                    onChange={(e) => setFormData((prev) => ({ ...prev, vectorStore: e.target.value as 'OPENSEARCH_SERVERLESS' | 'S3' }))}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <ThunderboltOutlined className="text-purple-500" />
                      Amazon OpenSearch Serverless <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full">Recomendado</span>
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Alto rendimiento, búsqueda híbrida. Ideal para consultas frecuentes.
                    </p>
                  </div>
                </label> */}

                <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <input
                    type="radio"
                    name="vectorStore"
                    value="S3"
                    checked={formData.vectorStore === 'S3'}
                    onChange={(e) => setFormData((prev) => ({ ...prev, vectorStore: e.target.value as 'OPENSEARCH_SERVERLESS' | 'S3' }))}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <FileTextOutlined className="text-blue-500" />
                      Amazon S3 Vectors
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Económico, ideal para grandes volúmenes con consultas esporádicas.
                    </p>
                  </div>
                </label>
              </div>
            </div>
            
            {/* Orígenes Permitidos */}
            <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Orígenes Permitidos *</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Dominios donde se usará el widget. Mínimo 1 requerido.</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <GlobalOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input type="text" value={newOrigin} onChange={(e) => setNewOrigin(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addOrigin())} className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white" placeholder="ejemplo.com o localhost:3000" />
                </div>
                <button type="button" onClick={addOrigin} disabled={!newOrigin.trim()} className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center gap-2">
                  <PlusOutlined /> Agregar
                </button>
              </div>
              {formData.allowedOrigins.length === 0 ? (
                <p className="text-xs text-red-500">Agrega al menos un dominio para continuar</p>
              ) : (
                <div className="space-y-2">
                  {formData.allowedOrigins.map((origin, index) => (
                    <div key={origin} className="flex items-center gap-3 px-4 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <GlobalOutlined className="text-green-500 flex-shrink-0" />
                      <span className="flex-1 text-sm font-mono text-gray-900 dark:text-white">{origin}</span>
                      <button onClick={() => removeOrigin(index)} className="p-1 text-gray-400 hover:text-red-500 transition-colors"><DeleteOutlined /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        )}

        {/* Step 3: Revisión */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="bg-gray-50 dark:bg-dark-bg rounded-lg p-6">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <RobotOutlined className="text-xl text-purple-500" />
                Resumen del servicio
              </h3>

              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400">Plan</span>
                  <span className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    {selectedPlan?.name}
                    {selectedPlan?.highlighted && (
                      <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full">
                        Popular
                      </span>
                    )}
                  </span>
                </div>

                <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400">Nombre del servicio</span>
                  <span className="font-medium text-gray-900 dark:text-white">{formData.serviceName}</span>
                </div>

                {formData.description && (
                  <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                    <span className="text-gray-600 dark:text-gray-400">Descripción</span>
                    <span className="font-medium text-gray-900 dark:text-white text-right max-w-xs truncate">
                      {formData.description}
                    </span>
                  </div>
                )}

                <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400">Mensajes/mes</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {selectedPlan?.messagesLimit.toLocaleString()}
                  </span>
                </div>

                <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400">Documentos</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {selectedPlan?.documentsLimit.toLocaleString()}
                  </span>
                </div>

                {selectedPlan?.storageMbLimit != null && (
                  <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                    <span className="text-gray-600 dark:text-gray-400">Almacenamiento</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {selectedPlan.storageMbLimit.toLocaleString()} MB
                    </span>
                  </div>
                )}

                {formData.apiAccess && (
                  <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                    <span className="text-gray-600 dark:text-gray-400">API Access</span>
                    <span className="font-medium text-green-600 dark:text-green-400">Habilitado</span>
                  </div>
                )}

                <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400">Orígenes permitidos</span>
                  <span className="font-medium text-gray-900 dark:text-white">{formData.allowedOrigins.length} dominio{formData.allowedOrigins.length !== 1 ? 's' : ''}</span>
                </div>

                <div className="py-2 border-b border-gray-200 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-400 text-sm">Features incluidas:</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedPlan?.featureLabels.map((label, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-full"
                      >
                        <CheckCircleFilled className="text-green-500 text-[10px]" />
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Precio */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-gray-900 dark:text-white">Costo mensual</h3>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {selectedPlan
                    ? selectedPlan.currency === 'MXN'
                      ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(selectedPlan.price)
                      : `$${selectedPlan.price}`
                    : '—'}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">/mes</span>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={step === 1}
          className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Anterior
        </button>

        <div className="flex gap-3">
          <button
            onClick={() => navigate('/chatbot-ia')}
            className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancelar
          </button>
          {step < 3 ? (
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={isCreating}
              className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isCreating ? (
                <>
                  <LoadingOutlined className="animate-spin" />
                  Creando servicio...
                </>
              ) : (
                <>
                  <RobotOutlined />
                  Crear Servicio AI
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateChatbotIA;
